import { useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  ChevronsUpDown,
  LogOut,
  User,
  Shield,
  Mail,
  Key,
  Building2,
  RefreshCw,
} from "lucide-react";

import { resetLoginCooldown, disableMsal, enableMsal } from "@/auth/useAuth";
import { Avatar, AvatarFallback } from "@/components/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/sidebar";
import { useKeycloak } from "@/providers/KeycloakProvider";
import { keycloakService } from "@/services/keycloakService";

export function NavUser() {
  const { isMobile } = useSidebar();
  const { instance, accounts } = useMsal();
  const { userProfile } = useKeycloak();

  const msalAccount = accounts[0];

  // Check if MSAL is disabled — but if MSAL already restored a live account,
  // clear the flag so the app behaves as logged in.
  const [msalDisabled, setMsalDisabled] = useState(() => {
    const disabled = localStorage.getItem("msalDisabled") === "true";
    if (disabled && msalAccount) {
      localStorage.removeItem("msalDisabled");
      return false;
    }
    return disabled;
  });

  const user = {
    name: userProfile?.firstName + " " + userProfile?.lastName,
    email: userProfile?.email,
    initials: `${userProfile?.firstName?.charAt(
      0
    )}${userProfile?.lastName?.charAt(0)}`,
  };

  const handleKeycloakLogout = () => {
    keycloakService.onAuthLogout();
  };

  const handleMsalLogout = async () => {
    try {
      // Disable MSAL to prevent automatic re-login attempts
      disableMsal();
      setMsalDisabled(true);

      resetLoginCooldown();
      await instance.logoutPopup({
        account: msalAccount,
      });
    } catch (error) {
      console.error("MSAL logout failed:", error);
      // Fallback to redirect logout
      await instance.logoutRedirect({
        account: msalAccount,
      });
    }
  };

  const handleMsalLogin = async () => {
    try {
      await instance.loginPopup();
    } catch (error) {
      console.error("MSAL login failed:", error);
    }
  };

  const handleMsalReEnable = () => {
    enableMsal();
    setMsalDisabled(false);
    window.location.reload(); // Refresh to allow login
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="h-8 w-8 rounded-lg bg-muted text-muted-foreground flex justify-center items-center flex-shrink-0">
                {user.initials}
              </div>

              <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                <span className="truncate font-semibold">{user.name}</span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[450px] rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex flex-col items-center gap-3 px-4 py-4">
                <Avatar className="h-16 w-16 rounded-full">
                  <AvatarFallback className="rounded-full text-lg font-semibold">
                    {user.initials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col items-center text-center gap-0.5">
                  <span className="font-semibold text-sm">{user.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {user.email}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />

            {/* Account Information Grid */}
            <div className="grid grid-cols-2 gap-3 px-3 py-3">
              {/* Keycloak Account */}
              <div className="space-y-2">
                <div className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                  <Key className="size-3.5" />
                  Keycloak Account
                </div>
                <div className="space-y-2 bg-muted/30 rounded-md p-2.5">
                  <div className="space-y-1">
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                      Username
                    </div>
                    <div className="text-xs font-medium truncate">
                      {userProfile?.username}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                      Name
                    </div>
                    <div className="text-xs font-medium truncate">
                      {user.name}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                      Email
                    </div>
                    <div className="text-xs font-medium truncate">
                      {user.email}
                    </div>
                  </div>
                </div>
              </div>

              {/* Microsoft Account */}
              {msalAccount ? (
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                    <Shield className="size-3.5" />
                    Microsoft Account
                  </div>
                  <div className="space-y-2 bg-muted/30 rounded-md p-2.5">
                    <div className="space-y-1">
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                        Name
                      </div>
                      <div className="text-xs font-medium truncate">
                        {msalAccount.name || "N/A"}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                        Email
                      </div>
                      <div className="text-xs font-medium truncate">
                        {msalAccount.username || "N/A"}
                      </div>
                    </div>
                    {msalAccount.tenantId && (
                      <div className="space-y-1">
                        <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
                          Tenant
                        </div>
                        <div className="text-[10px] font-mono truncate">
                          {msalAccount.tenantId}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                    <Shield className="size-3.5" />
                    Microsoft Account
                  </div>
                  <div className="bg-muted/30 rounded-md p-2.5 flex items-center justify-center min-h-[100px]">
                    <div className="text-xs text-muted-foreground text-center">
                      Not connected
                    </div>
                  </div>
                </div>
              )}
            </div>

            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer"
              onClick={handleKeycloakLogout}
            >
              <LogOut className="size-4" />
              Log out (Keycloak)
            </DropdownMenuItem>
            {msalAccount && (
              <DropdownMenuItem
                className="cursor-pointer text-orange-600 dark:text-orange-400 focus:text-orange-600 dark:focus:text-orange-400"
                onClick={handleMsalLogout}
              >
                <Shield className="size-4" />
                Log out (Microsoft)
              </DropdownMenuItem>
            )}

            {/* Connect Microsoft Account if not logged in */}
            {!msalAccount && !msalDisabled && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="cursor-pointer text-blue-600 dark:text-blue-400 focus:text-blue-600 dark:focus:text-blue-400"
                  onClick={handleMsalLogin}
                >
                  <Shield className="size-4" />
                  Connect Microsoft Account
                </DropdownMenuItem>
              </>
            )}

            {/* Re-enable MSAL if disabled */}
            {msalDisabled && !msalAccount && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="cursor-pointer text-green-600 dark:text-green-400 focus:text-green-600 dark:focus:text-green-400"
                  onClick={handleMsalReEnable}
                >
                  <RefreshCw className="size-4" />
                  Enable Microsoft Login
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
